import { useEffect, useState, useCallback } from "react";
import { useT } from "../lib/i18n";
import printApp1 from "../assets/print-app-1.png";
import printApp2 from "../assets/print-app-2.png";
import printApp3 from "../assets/print-app-3.png";

const INTERVAL_MS = 7000;

export function AppShowcase() {
  const t = useT();
  const slides = t.showcase.slides;

  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback(
    (next: number) => {
      if (next === current || animating) return;
      setAnimating(true);
      setTimeout(() => {
        setCurrent(next);
        setAnimating(false);
      }, 300);
    },
    [current, animating]
  );

  // Reset to slide 0 when language changes (slides array reference changes)
  useEffect(() => {
    setCurrent(0);
  }, [slides]);

  useEffect(() => {
    const id = setInterval(() => {
      goTo((current + 1) % slides.length);
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, [current, goTo, slides.length]);

  const slide = slides[current];
  const imgSrcs = [printApp1, printApp2, printApp3];

  return (
    <div className="mx-auto mt-16 max-w-6xl px-4 sm:px-0 text-left">
      {/* Screenshot frame sized for the current product captures. */}
      <div className="rounded-2xl border border-ink/10 shadow-2xl shadow-ink/15 overflow-hidden dark:border-paper/10 dark:shadow-black/40">
        <div className="relative aspect-[4/3] overflow-hidden bg-[#050505] sm:aspect-[16/10] lg:aspect-[4/3]">
          {imgSrcs.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={slides[i]?.alt ?? slides[i]?.heading ?? ""}
              className={[
                "absolute inset-0 w-full h-full object-contain transition-opacity duration-300",
                i === current && !animating ? "opacity-100" : "opacity-0",
              ].join(" ")}
            />
          ))}
        </div>
      </div>

      {/* Caption + dots */}
      <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-1">
        <div
          className={[
            "transition-all duration-300",
            animating ? "opacity-0 translate-y-1" : "opacity-100 translate-y-0",
          ].join(" ")}
        >
          <span className="inline-block font-mono text-[10px] uppercase tracking-widest text-ink/40 dark:text-paper/40 mb-1">
            {slide.tag}
          </span>
          <p className="font-display text-base font-semibold text-ink dark:text-paper leading-snug">
            {slide.heading}
          </p>
          <p className="mt-1 text-sm text-ink/60 dark:text-paper/60 max-w-md leading-relaxed">
            {slide.body}
          </p>
        </div>

        {/* Dot nav */}
        <div className="flex items-center gap-2 shrink-0">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
              className={[
                "rounded-full transition-all duration-300 focus:outline-none",
                i === current
                  ? "w-6 h-2 bg-ink dark:bg-paper"
                  : "w-2 h-2 bg-ink/20 hover:bg-ink/40 dark:bg-paper/20 dark:hover:bg-paper/40",
              ].join(" ")}
            />
          ))}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4 h-px w-full bg-ink/8 dark:bg-paper/8 overflow-hidden rounded-full">
        <div
          key={`${current}-${slide.tag}`}
          className="h-full bg-ink/30 dark:bg-paper/30 rounded-full"
          style={{ animation: `grow ${INTERVAL_MS}ms linear forwards` }}
        />
      </div>

      <style>{`
        @keyframes grow { from { width: 0% } to { width: 100% } }
      `}</style>
    </div>
  );
}
